import { useState, useEffect } from "react";
import { connectFTP } from "./ftpService";
import { motion } from "framer-motion";
import { LockClosedIcon, CheckCircleIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import logo from "../../assets/salon-bg.jpg";

export default function FTPConnect({ setIsConnected }) {
  const [credentials, setCredentials] = useState({ host: "", username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const savedHost = localStorage.getItem("ftpHost");
    const savedUser = localStorage.getItem("ftpUser");
    const savedPass = localStorage.getItem("ftpPass");

    if (savedHost && savedUser && savedPass) {
      setCredentials({ host: savedHost, username: savedUser, password: savedPass });
      setIsConnected(true);
    }
  }, [setIsConnected]);

  const handleConnect = async () => {
    setLoading(true);
    setError("");
  
    try {
      const response = await connectFTP({
        host: "151.80.152.214", // 🔐 Valeur fixe
        username: credentials.username,
        password: credentials.password,
      });
  
      if (response.data.success) {
        // Sauvegarde des identifiants en localStorage
        localStorage.setItem("ftpHost", "151.80.152.214");
        localStorage.setItem("ftpUser", credentials.username);
        localStorage.setItem("ftpPass", credentials.password);
        localStorage.setItem("ftpPath", response.data.cwd);
  
        setSuccess(true);
        setTimeout(() => {
          setIsConnected(true);
        }, 2000);
      } else {
        setError("Connexion échouée, vérifiez vos identifiants.");
      }
    } catch (error) {
      console.error("Erreur de connexion:", error);
      setError("Impossible de se connecter au serveur FTP.");
    }
  
    setLoading(false);
  };  

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="relative flex items-center justify-center min-h-[86vh] w-full bg-cover bg-center"
    >
      <div 
        className="absolute inset-0 bg-cover bg-center backdrop-blur-md" 
        style={{ backgroundImage: `url(${logo})`, height: "100%", filter: "blur(8px)" }}
      ></div>

      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut", type: "spring", stiffness: 100 }}
        className="p-8 rounded-lg shadow-lg w-96 text-white relative z-10 backdrop-blur-lg bg-gray-900/80"
      >
        <div className="flex justify-center mb-4">
          {success ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <CheckCircleIcon className="w-12 h-12 text-white animate-bounce" />
            </motion.div>
          ) : (
            <LockClosedIcon className="w-12 h-12 text-blue-500 animate-pulse" />
          )}
        </div>

        <h3 className="text-2xl font-bold mb-6 text-center">
          {success ? "Connexion réussie !" : "Connexion FTP"}
        </h3>

        {error && (
          <motion.p
            initial={{ x: -10 }}
            animate={{ x: 10 }}
            transition={{ repeat: 4, duration: 0.1, type: "spring" }}
            className="text-red-500 text-center mb-4"
          >
            {error}
          </motion.p>
        )}

        {!success && (
          <div className="space-y-4">
            <input
              type="text"
              value="151.80.152.214"
              readOnly
              tabIndex={-1}
              className="w-full p-3 bg-gray-800 text-gray-400 border border-gray-600 rounded text-sm cursor-not-allowed pointer-events-none"
            />
            <input
              type="text"
              placeholder="Nom d'utilisateur"
              className="w-full p-3 bg-gray-700 border border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 hover:bg-gray-600"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
            />

            {/* 🔐 Champ de mot de passe avec bouton Afficher/Masquer */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Mot de passe"
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 hover:bg-gray-600"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-200 transition"
              >
                {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
                className="w-4 h-4 text-blue-500 bg-gray-700 border border-gray-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="rememberMe" className="text-sm">
                Se souvenir de moi
              </label>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleConnect}
              disabled={loading}
              className={`w-full mt-6 py-3 rounded transition text-white ${
                loading
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {loading ? (
                <span className="flex justify-center items-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 0116 0H4z"
                    ></path>
                  </svg>
                  Connexion...
                </span>
              ) : (
                "Se connecter"
              )}
            </motion.button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
